import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, MapPin, Truck, ShieldAlert, Ticket, X, CheckCircle2, Percent } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { api, imageUrl, formatEUR } from "../lib/api";
import { useCart } from "../lib/cart-context";
import { useAuth } from "../lib/auth-context";
// Phase 35 — OriginSelector removed; envio shipping is weight-based (3 €/kg).

const DELIVERY_SLOTS = [
  "Mañana (9–13h)",
  "Tarde (16–20h)",
  "Sábado mañana (10–14h)",
];

// Fase 2 · Promo codes — helper de redondeo compartido con la UI.
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

export default function CheckoutPage() {
  const { cart, refresh: refreshCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [camps, setCamps] = useState([]);
  const [guestEmail, setGuestEmail] = useState("");
  const [senderName, setSenderName] = useState("");
  const [senderPhone, setSenderPhone] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [camp, setCamp] = useState("");
  const [daira, setDaira] = useState("");
  const [barrio, setBarrio] = useState("");
  const [deliverySlot, setDeliverySlot] = useState(DELIVERY_SLOTS[0]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Fase 1 · Payment methods toggle — lee el estado público antes de
  // renderizar el botón Stripe.
  const [paymentMethods, setPaymentMethods] = useState({
    card_enabled: true,
    cash_enabled: true,
    loaded: false,
  });

  useEffect(() => {
    api
      .get("/settings/payment-methods")
      .then((r) =>
        setPaymentMethods({
          card_enabled: !!r.data?.card_enabled,
          cash_enabled: !!r.data?.cash_enabled,
          loaded: true,
        })
      )
      .catch(() =>
        setPaymentMethods((p) => ({ ...p, loaded: true }))
      );
  }, []);

  // Fase 2 · Promo codes — estado local del código aplicado. `applied`
  // contiene el snapshot devuelto por /promo-codes/validate; se envía al
  // backend al crear la orden.
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // {code, discount_percent, discount_amount, new_total}
  const [promoError, setPromoError] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  // Solo la tienda `/envio` (shop=envio) admite promos. En `/local` la sección se oculta.
  const promoEligible = cart?.shop === "envio";

  // Recalcula el descuento en €EUR sobre el subtotal actual del carrito.
  const discountAmount = useMemo(() => {
    if (!promoApplied || !cart?.subtotal) return 0;
    return round2(cart.subtotal * (promoApplied.discount_percent / 100));
  }, [promoApplied, cart?.subtotal]);
  const finalTotal = useMemo(() => {
    if (!cart) return 0;
    return Math.max(round2((cart.total || 0) - discountAmount), 0);
  }, [cart, discountAmount]);

  const applyPromo = async () => {
    const code = (promoInput || "").trim().toUpperCase();
    if (!code) return;
    setPromoLoading(true);
    setPromoError(null);
    try {
      const { data } = await api.post("/promo-codes/validate", {
        code,
        context: "grocery",
        subtotal: cart.subtotal,
      });
      setPromoApplied({
        code: data.code,
        discount_percent: data.discount_percent,
        discount_amount: data.discount_amount,
      });
      toast.success(`${data.code} aplicado (-${data.discount_percent}%)`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Código no válido";
      setPromoError(typeof msg === "string" ? msg : "Código no válido");
      setPromoApplied(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromo = () => {
    setPromoApplied(null);
    setPromoInput("");
    setPromoError(null);
  };

  useEffect(() => {
    api.get("/delivery/camps").then((r) => setCamps(r.data.camps || [])).catch(() => {});
  }, []);

  // Ensure the page starts at the top on mount (safety net when navigated from mid-page)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Toast when the user returns from a cancelled Stripe session; strip the flag.
  useEffect(() => {
    if (searchParams.get("cancelled") === "1") {
      toast.info("Pago cancelado. Puedes intentarlo de nuevo.");
      const next = new URLSearchParams(searchParams);
      next.delete("cancelled");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Prefill sender from user profile
  useEffect(() => {
    if (user) {
      setSenderName((v) => v || user.name || "");
    }
  }, [user]);

  useEffect(() => {
    if (!cart.items || cart.items.length === 0) return;
    if (!cart.min_order_reached) {
      toast.error("El pedido mínimo es de 50 €");
    }
  }, [cart.items, cart.min_order_reached]);

  const validate = () => {
    if (!user && !/^\S+@\S+\.\S+$/.test(guestEmail))
      return "Introduce un email válido para el pedido";
    if (!senderName.trim()) return "Introduce el nombre del remitente";
    if (senderPhone.trim().length < 6) return "Teléfono del remitente no válido";
    if (!recipientName.trim()) return "Introduce el nombre del destinatario";
    if (recipientPhone.trim().length < 6) return "Teléfono del destinatario no válido";
    if (!camp) return "Selecciona un campamento";
    if (!daira.trim()) return "Introduce la daira";
    if (!barrio.trim()) return "Introduce el barrio";
    if (!deliverySlot) return "Selecciona una franja de entrega";
    if (!cart.min_order_reached) return "El pedido mínimo es de 50 €";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    // Phase 35 — the shopper_origin picker is gone from the UI; the cart
    // may not carry one anymore. `envio` shipping is now weight-based
    // (3 €/kg, Phase 34) so the field is only kept for backend/ order
    // history compatibility. Default to "spain" when unset — matches the
    // previous UX default and keeps the backend Pydantic model happy.
    const resolvedOrigin = cart.shopper_origin || "spain";
    setSubmitting(true);
    try {
      const body = {
        origin_url: window.location.origin,
        shopper_origin: resolvedOrigin,
        delivery: {
          camp,
          daira: daira.trim(),
          barrio: barrio.trim(),
          delivery_slot: deliverySlot,
          sender_name: senderName.trim(),
          sender_phone: senderPhone.trim(),
          recipient_name: recipientName.trim(),
          recipient_phone: recipientPhone.trim(),
        },
      };
      if (!user) body.guest_email = guestEmail.trim();
      if (promoApplied?.code) body.promo_code = promoApplied.code;
      const { data } = await api.post("/payments/order/checkout", body);
      // Redirect the browser to Stripe's hosted checkout page.
      window.location.href = data.url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo iniciar el pago";
      setError(typeof msg === "string" ? msg : "Error al iniciar el pago");
      toast.error(typeof msg === "string" ? msg : "Error al iniciar el pago");
      setSubmitting(false);
    }
  };

  const empty = !cart.items || cart.items.length === 0;

  if (empty) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="text-3xl font-black tracking-tight">
          Tu carro está vacío
        </h1>
        <p className="mt-3 text-neutral-500">
          Añade productos antes de tramitar tu pedido.
        </p>
        <Link
          to="/"
          className="mt-8 inline-block rounded-full bg-[#00953B] hover:bg-[#007a30] text-white px-6 py-3 font-semibold"
        >
          Volver al inicio
        </Link>
      </div>
    );
  }

  return (
    <div
      data-testid="checkout-page"
      className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10"
    >
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-8">
        Tramitar pedido
      </h1>
      {/* Phase 35 — the "Origen de compra" selector (España 12€ · Campamentos
          3.50€) was removed. In shop=envio the shipping cost is derived
          from the cart weight (3 €/kg, Phase 34) and no longer depends on
          the shopper's origin. The picker was only creating confusion. */}

      <form
        onSubmit={submit}
        className="grid lg:grid-cols-[1fr_360px] gap-8"
      >
        <div className="space-y-8">
          {/* Contacto */}
          <section className="rounded-2xl border border-neutral-200 bg-white p-6">
            <h2 className="text-xl font-black tracking-tight mb-4">1. Contacto</h2>
            {user ? (
              <div className="text-sm">
                <div className="text-neutral-500">Sesión iniciada como</div>
                <div className="font-semibold" data-testid="checkout-user-email">
                  {user.name} · {user.email}
                </div>
              </div>
            ) : (
              <div>
                <Label htmlFor="guest_email" className="text-sm">Email</Label>
                <Input
                  id="guest_email"
                  type="email"
                  required
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  data-testid="checkout-guest-email-input"
                  className="mt-1 h-11"
                  placeholder="tu@email.com"
                />
                <p className="text-xs text-neutral-500 mt-2">
                  Puedes crear una cuenta después para seguir tu pedido.
                </p>
              </div>
            )}
          </section>

          {/* Personas */}
          <section className="rounded-2xl border border-neutral-200 bg-white p-6">
            <h2 className="text-xl font-black tracking-tight mb-4">
              2. Remitente y destinatario
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sender_name" className="text-sm">Nombre del remitente</Label>
                <Input
                  id="sender_name"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  maxLength={80}
                  data-testid="checkout-sender-name"
                  className="mt-1 h-11"
                  placeholder="Tu nombre completo"
                />
              </div>
              <div>
                <Label htmlFor="sender_phone" className="text-sm">Teléfono del remitente</Label>
                <Input
                  id="sender_phone"
                  value={senderPhone}
                  onChange={(e) => setSenderPhone(e.target.value)}
                  maxLength={20}
                  data-testid="checkout-sender-phone"
                  className="mt-1 h-11"
                  placeholder="+34 600 000 000"
                />
              </div>
              <div>
                <Label htmlFor="recipient_name" className="text-sm">Nombre del destinatario</Label>
                <Input
                  id="recipient_name"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  maxLength={80}
                  data-testid="checkout-recipient-name"
                  className="mt-1 h-11"
                  placeholder="Nombre de tu familiar"
                />
              </div>
              <div>
                <Label htmlFor="recipient_phone" className="text-sm">Teléfono del destinatario</Label>
                <Input
                  id="recipient_phone"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  maxLength={20}
                  data-testid="checkout-recipient-phone"
                  className="mt-1 h-11"
                  placeholder="+213 …"
                />
              </div>
            </div>
            {user && (
              <p className="text-xs text-neutral-500 mt-3">
                Nombre del remitente prellenado desde tu cuenta.
              </p>
            )}
          </section>

          {/* Entrega */}
          <section className="rounded-2xl border border-neutral-200 bg-white p-6">
            <h2 className="text-xl font-black tracking-tight mb-4">3. Entrega</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-sm">Campamento</Label>
                <Select value={camp} onValueChange={setCamp}>
                  <SelectTrigger
                    data-testid="checkout-camp-select"
                    className="mt-1 h-11"
                  >
                    <SelectValue placeholder="Selecciona un campamento…" />
                  </SelectTrigger>
                  <SelectContent>
                    {camps.map((c) => (
                      <SelectItem
                        key={c}
                        value={c}
                        data-testid={`checkout-camp-option-${c}`}
                      >
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="daira" className="text-sm">Daira</Label>
                <Input
                  id="daira"
                  value={daira}
                  onChange={(e) => setDaira(e.target.value)}
                  maxLength={80}
                  data-testid="checkout-daira-input"
                  className="mt-1 h-11"
                  placeholder="Nombre de la daira"
                />
              </div>
              <div>
                <Label htmlFor="barrio" className="text-sm">Barrio</Label>
                <Input
                  id="barrio"
                  value={barrio}
                  onChange={(e) => setBarrio(e.target.value)}
                  maxLength={80}
                  data-testid="checkout-barrio-input"
                  className="mt-1 h-11"
                  placeholder="Nombre del barrio o bloque"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-sm">Franja de entrega</Label>
                <Select value={deliverySlot} onValueChange={setDeliverySlot}>
                  <SelectTrigger
                    data-testid="checkout-slot-select"
                    className="mt-1 h-11"
                  >
                    <SelectValue placeholder="Selecciona una franja…" />
                  </SelectTrigger>
                  <SelectContent>
                    {DELIVERY_SLOTS.map((s) => (
                      <SelectItem
                        key={s}
                        value={s}
                        data-testid={`checkout-slot-option-${s}`}
                      >
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {camp && (
              <div
                data-testid="checkout-camp-banner"
                className="mt-4 flex items-center gap-2 text-sm bg-[#F1FAF3] text-[#00753a] rounded-lg px-3 py-2"
              >
                <Truck className="h-4 w-4" />
                Entrega estimada a tu familia en <strong>{camp}</strong>.
              </div>
            )}
          </section>

          {/* Fase 2 · Promo codes — 4º paso, solo en shop=envio.
              Diseño prominente (dashed emerald) para que sea imposible
              saltárselo al scrollear el checkout. */}
          {promoEligible && (
            <section
              data-testid="checkout-promo-section"
              className={
                promoApplied
                  ? "rounded-2xl border-2 border-emerald-500 bg-emerald-100 p-5 sm:p-6"
                  : "rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50 p-5 sm:p-6"
              }
            >
              <div className="flex items-start gap-4">
                <div
                  className={
                    "shrink-0 rounded-2xl p-2 sm:p-3 " +
                    (promoApplied
                      ? "bg-emerald-500 text-white"
                      : "bg-white border border-emerald-200 text-emerald-600")
                  }
                >
                  {promoApplied ? (
                    <CheckCircle2 className="h-6 w-6 sm:h-8 sm:w-8" strokeWidth={2.25} />
                  ) : (
                    <Ticket className="h-6 w-6 sm:h-8 sm:w-8" strokeWidth={2.25} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg sm:text-xl font-black tracking-tight text-emerald-900">
                    4. ¿Tienes un código promocional?{" "}
                    <span className="text-emerald-700 font-semibold text-sm">
                      (opcional)
                    </span>
                  </h2>
                  <p className="text-xs sm:text-sm text-emerald-800/80 mt-1">
                    Ingresa tu código para obtener un descuento en tu pedido.
                  </p>

                  {promoApplied ? (
                    <div
                      data-testid="checkout-promo-applied-chip"
                      className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-white border border-emerald-300 px-4 py-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Percent className="h-5 w-5 text-emerald-600 shrink-0" />
                        <div className="min-w-0">
                          <div className="font-black font-mono text-emerald-900 truncate">
                            {promoApplied.code}
                          </div>
                          <div className="text-xs text-emerald-700 font-semibold">
                            Aplicado · descuento del {promoApplied.discount_percent}%
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={removePromo}
                        data-testid="checkout-promo-remove-btn"
                        className="rounded-full h-9 text-emerald-700 hover:text-red-600 hover:bg-red-50 font-semibold text-sm"
                        aria-label="Quitar código"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Quitar
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-4 flex gap-2">
                      <Input
                        value={promoInput}
                        onChange={(e) => {
                          setPromoError(null);
                          setPromoInput(
                            e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase()
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            applyPromo();
                          }
                        }}
                        maxLength={20}
                        placeholder="RAMADAN10"
                        className="h-11 font-mono uppercase bg-white border-emerald-200 focus-visible:ring-emerald-500"
                        data-testid="checkout-promo-input"
                      />
                      <Button
                        type="button"
                        onClick={applyPromo}
                        disabled={promoLoading || !promoInput.trim()}
                        data-testid="checkout-promo-apply-btn"
                        className="rounded-full h-11 px-5 bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap font-semibold"
                      >
                        {promoLoading ? "Comprobando…" : "Aplicar"}
                      </Button>
                    </div>
                  )}

                  {promoError && (
                    <div
                      data-testid="checkout-promo-error"
                      className="mt-3 text-sm text-red-700 bg-white border border-red-200 rounded-lg px-3 py-2"
                    >
                      {promoError}
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* 5. Método de pago — Fase 1 · toggle. Oculto cuando card_enabled=false. */}
          {paymentMethods.card_enabled && (
          <section
            data-testid="checkout-section-pago-tarjeta"
            className="rounded-2xl border border-neutral-200 bg-white p-6"
          >
            <h2 className="text-xl font-black tracking-tight mb-1">
              5. Método de pago
            </h2>
            <p className="text-xs text-neutral-500 mb-4">
              Al confirmar tu pedido, te llevaremos a la página segura de Stripe
              para introducir los datos de tu tarjeta.
            </p>
            <div className="flex items-center gap-3 text-sm bg-neutral-50 border border-neutral-100 rounded-xl px-4 py-3">
              <Lock className="h-4 w-4 text-neutral-500 shrink-0" />
              <div className="text-neutral-600">
                Tus datos se procesan en el entorno seguro de Stripe. MercaGo
                no almacena tu tarjeta.
              </div>
            </div>
          </section>
          )}

          {/* Fase 1 · Banner cuando ambos métodos están OFF (grocery solo
              soporta tarjeta hoy, así que basta con detectar card_enabled=false). */}
          {paymentMethods.loaded && !paymentMethods.card_enabled && (
            <section
              data-testid="checkout-payments-unavailable-banner"
              className="rounded-2xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-3"
            >
              <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <strong>Pagos temporalmente no disponibles.</strong> Vuelve
                más tarde para completar tu pedido.
              </div>
            </section>
          )}
        </div>

        {/* Summary sidebar */}
        <aside className="lg:sticky lg:top-24 lg:self-start rounded-2xl border border-neutral-200 bg-white p-6">
          <h2 className="text-xl font-black tracking-tight mb-4">Resumen</h2>
          <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
            {cart.items.map((it) => (
              <div key={it.product_id} className="flex gap-3 items-start" data-testid={`checkout-line-${it.product_id}`}>
                <div className="h-12 w-12 rounded-lg bg-neutral-50 border border-neutral-100 shrink-0 overflow-hidden">
                  {it.product?.image_url && (
                    <img
                      src={imageUrl(it.product.image_url)}
                      alt={it.product.name}
                      className="h-full w-full object-contain"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0 text-sm">
                  <div className="line-clamp-2 leading-tight">{it.product?.name}</div>
                  <div className="text-xs text-neutral-500">x{it.quantity}</div>
                </div>
                <div className="text-sm font-semibold">{formatEUR(it.line_total)}</div>
              </div>
            ))}
          </div>
          <Separator className="my-4" />
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-neutral-600">Subtotal</span>
              <span data-testid="checkout-subtotal">{formatEUR(cart.subtotal)}</span>
            </div>
            {cart.shop === "envio" && cart.total_weight_kg > 0 && (
              <div className="flex justify-between text-xs text-neutral-500">
                <span>Peso total del pedido</span>
                <span data-testid="checkout-total-weight">
                  {cart.total_weight_kg.toFixed(2)} kg
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-neutral-600">
                {cart.shipping_label || "Envío"}
              </span>
              <span data-testid="checkout-shipping">{formatEUR(cart.shipping_cost)}</span>
            </div>
            {/* Fase 2 · Promo codes — línea de descuento en el resumen. */}
            {promoApplied && discountAmount > 0 && (
              <div
                className="flex justify-between text-emerald-700"
                data-testid="checkout-summary-discount-line"
              >
                <span className="font-semibold flex items-center gap-1">
                  <Ticket className="h-3.5 w-3.5" />
                  {promoApplied.code} (-{promoApplied.discount_percent}%)
                </span>
                <span className="font-semibold">-{formatEUR(discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-base pt-1">
              <span>Total</span>
              <span data-testid="checkout-total">{formatEUR(finalTotal)}</span>
            </div>
          </div>

          {error && (
            <div
              data-testid="checkout-error"
              className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
            >
              {error}
            </div>
          )}

          {/* Fase 2 · Recordatorio del código promo en el aside, para no
              perderlo de vista al scrollear (especialmente en móvil, donde
              el aside cae debajo del formulario). Solo si eres elegible y
              todavía no has aplicado ningún código. */}
          {promoEligible && !promoApplied && (
            <div
              data-testid="checkout-summary-promo-hint"
              className="mt-4 flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-dashed border-emerald-300 rounded-lg px-3 py-2"
            >
              <Ticket className="h-4 w-4 shrink-0" />
              <span>
                ¿Tienes un código?{" "}
                <a
                  href="#promo"
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.querySelector(
                      '[data-testid="checkout-promo-input"]'
                    );
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth", block: "center" });
                      el.focus();
                    }
                  }}
                  className="font-bold underline hover:text-emerald-900"
                >
                  Aplícalo aquí
                </a>
                .
              </span>
            </div>
          )}

          <Button
            type="submit"
            disabled={
              submitting ||
              !cart.min_order_reached ||
              !paymentMethods.card_enabled
            }
            data-testid="checkout-stripe-btn"
            className="mt-4 w-full h-12 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white font-semibold text-base disabled:opacity-50"
          >
            {submitting
              ? "Redirigiendo…"
              : paymentMethods.card_enabled
              ? "Pagar con Stripe"
              : "Pagos no disponibles"}
          </Button>
          <p className="mt-3 text-[11px] text-neutral-500 text-center flex items-center justify-center gap-1">
            <Lock className="h-3 w-3" />
            Serás redirigido a la página segura de Stripe para completar el pago.
          </p>
        </aside>
      </form>
    </div>
  );
}
